import React, { useEffect, useState } from "react";
import AdminLayout from "@/components/AdminLayout";
import api from "@/lib/api";
import { Link } from "react-router-dom";
import { Users, DollarSign, FlaskConical, Sparkles, TrendingDown, UserPlus, AlertTriangle } from "lucide-react";

export default function AdminOverview() {
  const [data, setData] = useState(null);
  const [retention, setRetention] = useState(null);

  useEffect(() => {
    api.get("/admin/overview").then((r) => setData(r.data)).catch(() => setData(null));
    api.get("/admin/audit-retention").then((r) => setRetention(r.data)).catch(() => setRetention(null));
  }, []);

  return (
    <AdminLayout>
      <p className="label-eyebrow mb-2">Admin · Overview</p>
      <h1 className="font-serif text-4xl text-[#2D322B] leading-tight mb-8">A glance at the grove.</h1>

      {retention?.warning && (
        <div className="mb-8 flex items-start gap-3 bg-[#FBEFD6] border border-[#E3C77E] rounded-xl p-4" data-testid="overview-retention-warning">
          <AlertTriangle className="w-5 h-5 text-[#8a6620] shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="font-medium text-[#6b4f12]">Data retention expiry approaching</p>
            <p className="text-sm text-[#8a6620] mt-1">
              {retention.expiring_soon_count} health-data record(s) are within {retention.warn_window_days} days
              of the {retention.retention_years}-year retention limit.
            </p>
          </div>
          <Link to="/admin/audit" className="text-xs uppercase tracking-[2px] text-[#8a6620] hover:text-[#6b4f12] whitespace-nowrap pt-1" data-testid="overview-retention-link">Review →</Link>
        </div>
      )}

      {!data ? <p className="text-[#9A8F7E]">Loading…</p> : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8" data-testid="overview-kpis">
            <Stat icon={Users}        label="Total members"        value={data.members.total} />
            <Stat icon={DollarSign}   label="MRR (AUD)"            value={`$${data.revenue.mrr_aud.toLocaleString()}`} />
            <Stat icon={UserPlus}     label="New this month"       value={data.members.new_this_month} />
            <Stat icon={TrendingDown} label="Payment fails (30d)"  value={data.members.payment_failures_30d} accent={data.members.payment_failures_30d > 0} />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
            <div className="bg-white border border-[#E8DFD0] p-6" data-testid="overview-plans">
              <p className="label-eyebrow mb-4">Members by plan</p>
              <div className="space-y-3">
                {Object.entries(data.members.by_plan).map(([plan, n]) => (
                  <div key={plan} className="flex items-center justify-between">
                    <p className="font-serif text-base text-[#2D322B] capitalize">{plan}</p>
                    <p className="text-[#5C6359]">{n}</p>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-white border border-[#E8DFD0] p-6" data-testid="overview-rx">
              <div className="flex items-center justify-between mb-4">
                <p className="label-eyebrow">Prescription requests</p>
                <Link to="/admin/prescriptions" className="text-xs uppercase tracking-[2px] text-[#C05C42] hover:text-[#A04A33]" data-testid="overview-rx-link">Review →</Link>
              </div>
              <div className="grid grid-cols-4 gap-3">
                {[
                  { key: "pending",    label: "Pending"    },
                  { key: "reviewing",  label: "Reviewing"  },
                  { key: "quoted",     label: "Quoted"     },
                  { key: "replied",    label: "Replied"    },
                ].map(({ key, label }) => {
                  const n = data.rx_requests[key] || 0;
                  const highlight = key === "pending" && n > 0;
                  return (
                    <div key={key} className={`p-3 text-center border ${highlight ? "border-[#C05C42] bg-[#FAF1ED]" : "border-[#E8DFD0]"}`}>
                      <p className={`font-serif text-2xl ${highlight ? "text-[#C05C42]" : "text-[#2D322B]"}`}>{n}</p>
                      <p className="text-xs text-[#9A8F7E] mt-1 uppercase tracking-widest">{label}</p>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          <div className="bg-white border border-[#E8DFD0] p-6" data-testid="overview-apothecary">
            <div className="flex items-center justify-between mb-4">
              <p className="label-eyebrow">Apothecary, last 30 days</p>
              <Link to="/admin/analytics" className="text-xs uppercase tracking-[2px] text-[#C05C42] hover:text-[#A04A33]">Funnel →</Link>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="flex items-center gap-3">
                <Sparkles className="w-4 h-4 text-[#C05C42]" strokeWidth={1.5} />
                <p className="text-sm text-[#5C6359]">Massey mentions</p>
                <p className="ml-auto font-serif text-2xl text-[#2D322B]">{data.apothecary.massey_mentions_30d}</p>
              </div>
              <div className="flex items-center gap-3">
                <FlaskConical className="w-4 h-4 text-[#C05C42]" strokeWidth={1.5} />
                <p className="text-sm text-[#5C6359]">Total clicks</p>
                <p className="ml-auto font-serif text-2xl text-[#2D322B]">{data.apothecary.clicks_30d}</p>
              </div>
            </div>
          </div>
        </>
      )}
    </AdminLayout>
  );
}

function Stat({ icon: Icon, label, value, accent }) {
  return (
    <div className={`p-5 border ${accent ? "border-[#C05C42] bg-[#FAF1ED]" : "border-[#E8DFD0] bg-white"}`}>
      <div className="flex items-center gap-2 mb-2">
        <Icon className={`w-4 h-4 ${accent ? "text-[#C05C42]" : "text-[#5C6359]"}`} strokeWidth={1.5} />
        <p className="label-eyebrow">{label}</p>
      </div>
      <p className={`font-serif text-3xl ${accent ? "text-[#C05C42]" : "text-[#2D322B]"}`}>{value}</p>
    </div>
  );
}
